import type { FormEvent } from 'react';
import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import { Textarea } from '@/components/ui';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { VoiceButton } from './VoiceButton';
import { ChatInputShell } from './ChatInputShell';
import type { ChatInputProps, MentionOption } from './types';
import { usePushToTalk } from '@/hooks/usePushToTalk';
import { RecordingInterface } from './RecordingInterface';
import { useUiStore } from '@/store/uiStore';
import { EchoAvatar } from '@/components/common/EchoAvatar';

/**
 * Chat input for the logged-in messenger. Handles text submit, push-to-talk
 * voice recording (mobile), inline voice recording (desktop), and the @mention
 * dropdown for group chats.
 *
 * Push-to-talk timing + the gradient-border / glow chrome live in
 * `usePushToTalk` and `ChatInputShell`; this component just composes them.
 */
export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  onSendAudio,
  isLoading = false,
  className,
  isTransitioning = false,
  mentions,
}) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [, setError] = React.useState<unknown>(null);
  const openModal = useUiStore((state) => state.openModal);

  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionTriggerPos = useRef<number>(-1);

  // Autofocus only on desktop (xl breakpoint = 1280px). Lazy init runs once.
  const [shouldAutoFocus] = useState(() => {
    if (typeof window !== 'undefined') return window.innerWidth >= 1280;
    return false;
  });

  const {
    isRecording,
    recordingTime,
    audioData,
    startRecording,
    stopRecording,
    cancelRecording,
    handleTouchStart,
  } = usePushToTalk({
    ...(onSendAudio !== undefined ? { onSendAudio } : {}),
    disabled: isLoading || isSending,
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const trimmedMessage = message.trim();
    if (!trimmedMessage || isSending || isLoading) return;

    setIsSending(true);
    setMessage('');
    try {
      await onSend(trimmedMessage);
    } catch (error) {
      setError(error);
    } finally {
      setIsSending(false);
    }
  };

  const filteredMentions = React.useMemo<MentionOption[]>(() => {
    if (mentionQuery === null || !mentions || mentions.length === 0) return [];
    if (mentionQuery === '') return mentions;
    const query = mentionQuery.toLowerCase();
    return mentions.filter((mention) => mention.name.toLowerCase().includes(query));
  }, [mentionQuery, mentions]);

  const isMentionOpen = filteredMentions.length > 0;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setMessage(val);

    if (!mentions || mentions.length === 0) {
      setMentionQuery(null);
      return;
    }

    const cursorPos = e.target.selectionStart ?? val.length;
    const textBeforeCursor = val.slice(0, cursorPos);
    const atIdx = textBeforeCursor.lastIndexOf('@');

    // Trigger only on a `@` that begins a word (start of input, after space, or after newline).
    if (
      atIdx === -1 ||
      (atIdx > 0 && textBeforeCursor[atIdx - 1] !== ' ' && textBeforeCursor[atIdx - 1] !== '\n')
    ) {
      setMentionQuery(null);
      return;
    }

    const query = textBeforeCursor.slice(atIdx + 1);
    if (query.includes(' ')) {
      setMentionQuery(null);
      return;
    }

    mentionTriggerPos.current = atIdx;
    setMentionQuery(query);
    setMentionIndex(0);
  };

  const selectMention = (option: MentionOption) => {
    const atIdx = mentionTriggerPos.current;
    if (atIdx === -1) return;
    const before = message.slice(0, atIdx);
    const textarea = document.getElementById('message-input') as HTMLTextAreaElement | null;
    const cursorPos = textarea?.selectionStart ?? message.length;
    const after = message.slice(cursorPos);
    const inserted = `@${option.name} `;
    setMessage(before + inserted + after);
    setMentionQuery(null);

    requestAnimationFrame(() => {
      if (textarea) {
        const newPos = before.length + inserted.length;
        textarea.focus();
        textarea.setSelectionRange(newPos, newPos);
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isMentionOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((prev) => (prev + 1) % filteredMentions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((prev) => (prev - 1 + filteredMentions.length) % filteredMentions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const selected = filteredMentions[mentionIndex];
        if (selected) selectMention(selected);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionQuery(null);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as FormEvent);
    }
  };

  const hasText = message.trim().length > 0;

  return (
    <ChatInputShell
      onSubmit={handleSubmit}
      {...(className !== undefined ? { className } : {})}
      expandHeight={isRecording}
      glowFaded={isTransitioning || isRecording}
      disclaimer={
        !isRecording && (
          <p className='mt-3 text-center font-sans text-[12px] font-medium leading-160 tracking-neg-2 text-gray-610'>
            This is an Echo AI Clone. Echoes can make mistakes. Check important info. See{' '}
            <span
              onClick={() => openModal('echoDetails')}
              className='cursor-pointer font-bold underline decoration-solid decoration-auto hover:text-white/80'
            >
              Echoes Details
            </span>
            .
          </p>
        )
      }
    >
      {isRecording ? (
        <RecordingInterface
          recordingTime={recordingTime}
          audioData={audioData}
          onStop={async () => {
            const result = await stopRecording();
            if (result && onSendAudio) {
              onSendAudio(result.blob, audioData, result.duration);
            }
          }}
          onCancel={cancelRecording}
        />
      ) : (
        <>
          {isMentionOpen && (
            <div
              className={clsx(
                'absolute inset-x-4 bottom-full z-50 mb-2',
                'overflow-hidden rounded-16',
                'border border-white/10 bg-brand-800/95 backdrop-blur-md',
                'shadow-lg'
              )}
            >
              {filteredMentions.map((option, idx) => (
                <button
                  key={option.id}
                  type='button'
                  onMouseDown={(e) => {
                    // Prevent textarea blur — we need to insert into it after the click.
                    e.preventDefault();
                    selectMention(option);
                  }}
                  className={clsx(
                    'flex w-full items-center gap-3 px-4 py-3',
                    'font-sans text-14 font-medium text-white',
                    'transition-colors',
                    idx === mentionIndex ? 'bg-white/10' : 'hover:bg-white/5'
                  )}
                >
                  <EchoAvatar src={option.avatarUrl ?? null} alt={option.name} size={32} />
                  <span>{option.name}</span>
                </button>
              ))}
            </div>
          )}

          <Textarea
            id='message-input'
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={
              mentions && mentions.length > 0 ? 'Type @ to mention an echo' : 'Enter Message'
            }
            className={clsx(
              'size-full resize-none',
              'border-none bg-transparent shadow-none',
              'font-sans !text-16 font-medium leading-160 tracking-[-0.02em]',
              'text-white placeholder:text-white/50',
              'py-[22px] pl-[30px] pr-[120px]',
              'focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0'
            )}
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              height: '68px',
            }}
            autoFocus={shouldAutoFocus}
          />
          <style>{`
            #message-input::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          {hasText ? (
            <button
              type='submit'
              disabled={isLoading || isSending}
              className='absolute right-6 top-1/2 -translate-y-1/2 text-accent-300 transition-colors hover:text-accent-200 disabled:opacity-50'
            >
              <PaperAirplaneIcon className='size-6' />
            </button>
          ) : (
            <VoiceButton
              onClick={startRecording}
              onTouchStart={handleTouchStart}
              disabled={isLoading || isSending}
            />
          )}
        </>
      )}
    </ChatInputShell>
  );
};

ChatInput.displayName = 'ChatInput';
